import React from 'react'
import { theme } from 'antd'
import { fontSize, fontWeight, radius } from '@vacationtracker/shared/constants/ThemeSetting'
import { UserAvatar } from '@vacationtracker/shared/components/user-avatar'

const MAX_AVATARS = 3
const AVATAR_SIZE = 32
const RING_WIDTH = 2
const OVERLAP = -6

export interface AvatarStackUser {
  id: string
  name: string
  imageUrl?: string
}

export interface AvatarStackProps {
  users: AvatarStackUser[]
  /**
   * What the stack says out loud, e.g. "7 employees". Only the first few users are drawn and the
   * rest collapse into a +N chip, so the whole thing is one image as far as a screen reader is
   * concerned - without this the count would be unreadable.
   */
  label: string
}

/** Overlapping avatars with a +N overflow chip. */
export function AvatarStack({ users, label }: AvatarStackProps) {
  const { token } = theme.useToken()
  const shown = users.slice(0, MAX_AVATARS)
  const overflow = users.length - shown.length

  if (shown.length === 0) return null

  const size = AVATAR_SIZE
  const ringColor = token.colorBgContainer

  return (
    <span role="img" aria-label={label} style={{ display: 'inline-flex', alignItems: 'center', flex: 'none' }}>
      {shown.map((user, index) => (
        <span
          key={user.id}
          style={{
            display: 'inline-flex',
            borderRadius: radius.full,
            boxShadow: `0 0 0 ${RING_WIDTH}px ${ringColor}`,
            marginLeft: index === 0 ? 0 : OVERLAP,
          }}
        >
          <UserAvatar id={user.id} name={user.name} avatar={user.imageUrl} shape="circle" avatarSize={size} />
        </span>
      ))}
      {overflow > 0 && (
        <span
          style={{
            width: size,
            height: size,
            marginLeft: OVERLAP,
            borderRadius: radius.full,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: token.colorBgLayout,
            color: token.colorTextSecondary,
            fontSize: fontSize.sm,
            fontWeight: fontWeight.medium,
            boxShadow: `0 0 0 ${RING_WIDTH}px ${ringColor}`,
          }}
        >
          {`+${overflow}`}
        </span>
      )}
    </span>
  )
}
